/**
 * Helpers to normalize a Google Places `PlaceResult` into the flat address
 * shape used by our expedition forms.
 *
 * The main subtlety is that the position of the house number relative to the
 * street name depends on the country: most Anglophone countries and France
 * write it first ("12 Rue de la Paix", "221B Baker Street") while much of
 * continental Europe and Latin America write it last ("Hauptstraße 5",
 * "Via Roma 10"). Building the street line naively as `number + route` produces
 * wrong-looking addresses for those countries.
 */

export interface ParsedGoogleAddress {
  street: string;
  city: string;
  postalCode: string;
  /** ISO 3166-2 subdivision short code (e.g. "CA", "NY"), when available. */
  stateOrProvinceCode: string;
  /** ISO 3166-1 alpha-2 country code derived from the place, when available. */
  countryCode: string;
}

/**
 * Countries where the house number is written *after* the street name.
 * Everything else defaults to number-first, which matches FR / US / GB / CA /
 * and most of our shipping destinations.
 */
const STREET_NUMBER_AFTER_COUNTRIES = new Set([
  // Western & Central Europe
  "DE",
  "AT",
  "CH",
  "LI",
  "NL",
  "BE",
  "LU",
  // Nordics
  "DK",
  "SE",
  "NO",
  "FI",
  "IS",
  // Central & Eastern Europe / Balkans
  "PL",
  "CZ",
  "SK",
  "HU",
  "HR",
  "SI",
  "RS",
  "BA",
  "ME",
  "MK",
  "AL",
  "RO",
  "BG",
  "GR",
  // Southern Europe
  "PT",
  "IT",
  "ES",
  // Turkey
  "TR",
  // Latin America
  "BR",
  "AR",
  "CL",
  "CO",
  "PE",
  "UY",
  "PY",
  "BO",
  "EC",
  "VE",
  "MX",
]);

/**
 * Component types used to resolve the city, from most to least specific.
 * The leveled sublocality types are preferred over the generic `sublocality`
 * one, since in some countries (e.g. Japan) several components share the
 * generic tag and a plain lookup would return a block number instead of a ward.
 */
const CITY_COMPONENT_TYPES = [
  "locality",
  "postal_town",
  "sublocality_level_1",
  "sublocality",
  "administrative_area_level_3",
  "administrative_area_level_2",
  "administrative_area_level_1",
] as const;

/**
 * Components that make up the street line for addresses without a `route`
 * (e.g. Japan), ordered from the most specific unit to the broadest area.
 * Higher sublocality levels cover smaller areas, so they come first.
 */
const STREET_FALLBACK_COMPONENT_TYPES = [
  "subpremise",
  "premise",
  "sublocality_level_5",
  "sublocality_level_4",
  "sublocality_level_3",
  "sublocality_level_2",
  "sublocality_level_1",
] as const;

/**
 * City-states and territories where Google often returns no locality-like
 * component: the country name itself is used as the city.
 */
const CITY_STATE_COUNTRIES = new Set(["MO", "HK", "SG", "MC", "VA", "GI"]);

const getComponent = (
  place: google.maps.places.PlaceResult,
  type: string,
): google.maps.GeocoderAddressComponent | undefined =>
  place.address_components?.find((component) => component.types.includes(type));

const getLongName = (place: google.maps.places.PlaceResult, type: string): string =>
  getComponent(place, type)?.long_name ?? "";

const getShortName = (place: google.maps.places.PlaceResult, type: string): string =>
  getComponent(place, type)?.short_name ?? "";

/** Compose the street line with the country-appropriate number/route ordering. */
export function buildStreetLine(streetNumber: string, route: string, countryCode: string): string {
  if (!streetNumber) return route;
  if (!route) return streetNumber;

  return STREET_NUMBER_AFTER_COUNTRIES.has(countryCode.toUpperCase())
    ? `${route} ${streetNumber}`
    : `${streetNumber} ${route}`;
}

/**
 * Build the street line for addresses that have no `route` (e.g. Japan), where
 * the location is expressed through sublocality/premise components. `excludeType`
 * is the component type already consumed by the city, so it isn't duplicated.
 */
function buildStreetFromComponents(
  place: google.maps.places.PlaceResult,
  excludeType: string,
): string {
  const seen = new Set<string>();

  return STREET_FALLBACK_COMPONENT_TYPES.filter((type) => type !== excludeType)
    .map((type) => getLongName(place, type))
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    })
    .join(" ");
}

/**
 * Parse a Google Places result into our flat address shape.
 *
 * @param fallbackCountryCode country code known by the form, used to pick the
 * street ordering when the place itself doesn't expose a country component.
 */
export function parseGooglePlaceAddress(
  place: google.maps.places.PlaceResult | null,
  fallbackCountryCode = "",
): ParsedGoogleAddress | null {
  if (!place?.address_components) return null;

  const countryCode = getShortName(place, "country") || fallbackCountryCode;

  let city = "";
  let cityType = "";
  for (const type of CITY_COMPONENT_TYPES) {
    const value = getLongName(place, type);
    if (value) {
      city = value;
      cityType = type;
      break;
    }
  }

  if (!city && CITY_STATE_COUNTRIES.has(countryCode.toUpperCase())) {
    city = getLongName(place, "country");
  }

  const streetNumber = getLongName(place, "street_number");
  const route = getLongName(place, "route");
  const street =
    (route
      ? buildStreetLine(streetNumber, route, countryCode)
      : buildStreetFromComponents(place, cityType)) ||
    place.name ||
    "";

  return {
    street,
    city,
    postalCode: getLongName(place, "postal_code"),
    stateOrProvinceCode: getShortName(place, "administrative_area_level_1"),
    countryCode,
  };
}
